<script lang="ts" setup>
import {
  computed,
  ComputedRef,
  CSSProperties,
  inject,
  nextTick,
  onMounted,
  onUnmounted,
  PropType,
  Ref,
  ref,
  useSlots,
  watch,
} from 'vue';
import {
  TirPmIconView,
  TirPmContextMenuMouseButtonEnum,
  TirPmContextMenuButton,
  TirPmNotificationEventBus,
  TirPmNotificationModel,
  TirPmTooltipElement,
  TirPmContextMenu,
  TirPmRouterLink,
} from 'tir-components';
import { ListBulletIcon, Bars3BottomLeftIcon } from 'tir-style-system/icons/solid';
import { RouterLink, useRouter } from 'vue-router';
import { RemoveTaskModal } from '@/components';
import NamespaceIconView from '@/pages/project-namespaces-page/components/namespace-card/namespace-icon-view.vue';
import { TaskModel } from '@/components/tasks-kanban/models';
import { ListItemModel } from '@/components/tir-pm-list/models';
import { TaskViewElementEnum } from '@/components/tasks-kanban/enums';
import { OrderTaskModel, TaskColumnModel, TeammateModel } from '../../models';
import {
  TaskCardDifficulty,
  TaskCardEstimateTime,
  TaskCardPriority,
  TaskCardStatus,
  TaskCardWorkTime,
  TaskCardPerformers,
} from '@/components/tir-pm-list/components/task-card/components';
import { copyToClipboard } from '@/components/task-forms/utils';
import {
  ArrowTopRightOnSquareIcon,
  ClipboardDocumentIcon,
  CurtainIcon,
  MapIcon,
  TrashIcon,
} from 'tir-style-system/icons/outline';
import { CurrentUser } from 'tir-pm-user-core';
import GetEmployeesWithAccessToProjectItemModel from '@/pages/monitorings/models/GetEmployeesWithAccessToProjectItemModel';
import PerformerListByOrder from '@/components/tasks-kanban/components/PerformerListByOrder.vue';
import { TaskHistoryOrder } from './components';
import { ProjectModel } from '@/models';

/**Слоты */
const slots = useSlots();
/**Роутер */
const router = useRouter();

// #region defineProps
/**Параметры */
const props = defineProps({
  /**Юзер */
  user: { type: Object as PropType<TeammateModel> },
  /**Укороченная информация параметров */
  shortInfoParameter: {
    type: Boolean,
    default: false,
  },
  /**Данные задачи */
  task: { type: Object as PropType<OrderTaskModel>, required: true },
  /**Колонки */
  columns: { type: Array as PropType<TaskColumnModel[]>, default: () => [] },
  /**Страница Все задачи текущего юзера */
  checkAllTasks: { type: Boolean as PropType<boolean>, default: false },
  /**Настройки отображения */
  viewSettings: {
    type: Array as PropType<Array<TaskViewElementEnum>>,
    default: () => [
      TaskViewElementEnum.Namespace,
      TaskViewElementEnum.Queue,
      TaskViewElementEnum.Number,
      TaskViewElementEnum.Name,
      TaskViewElementEnum.Status,
      TaskViewElementEnum.Difficulty,
      TaskViewElementEnum.Priority,
      TaskViewElementEnum.EstimateTime,
      TaskViewElementEnum.WorkTime,
    ],
  },
  /**Выбранный исполнитель */
  selectPerformer: { type: Object as PropType<GetEmployeesWithAccessToProjectItemModel> },
  /**Ширина карточки */
  width: { type: String, default: '100%' },
  /**Дизейбл */
  isDisabled: {
    type: Boolean,
    default: false,
  },
  /**Возможность форматировать очередность */
  changeAccess: {
    type: Boolean,
  },
  /**Заливка */
  isFill: {
    type: Boolean,
    default: false,
  },
  /**Слот слева всегда видим */
  slotLeftVisible: {
    type: Boolean,
    default: false,
  },
  /**Таблица на странице мониторинга по тикетам */
  monitoringTicketPage: { type: Boolean, default: false },
  /**Скрыть пункты Открыть в шторке и Удалить */
  hideOpenAndDelete: { type: Boolean, default: false },
  /**Расположить статус в левой части таблицы */
  isStatusLeftBlock: { type: Boolean, default: false },
  /**Скрыть иконку у названия */
  hideIconName: { type: Boolean, default: false },
  /**Таблица на странице мониторинга по тикетам */
  monitoringPage: { type: Boolean, default: false },
});
// #endregion defineProps

// #region defineEmits
const emit = defineEmits<{
  /**Событие открытия шторки задачи */
  openTaskDrawer: [taskId: number];
  /**Удаление задачи */
  removeTask: [taskId: number];
}>();
// #endregion defineEmits

// #region Переменные
const parameterTypes = [
  TaskViewElementEnum.Difficulty,
  TaskViewElementEnum.Performers,
  TaskViewElementEnum.Priority,
  TaskViewElementEnum.Status,
  TaskViewElementEnum.EstimateTime,
  TaskViewElementEnum.WorkTime,
];
// #endregion Переменные

// #region refs
/**Ссылка на компонент контекстного меню */
const $contextMenu = ref<InstanceType<typeof TirPmContextMenu>>();
/**Отображать схему задачи */
const showSchema = ref(false);
/**Элемент обертки */
const $wrapper = inject('wrapperElement') as Ref<HTMLDivElement>;

/**Является ли юзер админом проекта */
const isProjectAdmin = inject('isAdminProject') as Ref<boolean>;
/**Является ли юзер овнером */
const isOwner = ref(CurrentUser.Roles.includes(2));
/**Окно удаления задачи */
const $removeTaskModal = ref<InstanceType<typeof RemoveTaskModal>>();
/**Перетаскивается ли элемент */
const isDragging = ref(false);
/**Является ли элмент ссылкой */
const isLink = ref(true);

// #endregion refs

// #region computed
/**Активен левый слот */
const haveLeftSlot = computed(() => slots.left?.());
/**Минимальная ширина */
const minWidth = computed(() => {
  if (props.columns.some((column) => !column.MinWidth)) return 'auto';

  const columnsCalcStr = props.columns.map((column) => column.MinWidth).join(' + ');

  return `calc(${columnsCalcStr} + ${0.75 * (props.columns.length - 1)}rem + 1.25rem)`;
});
/**Стили карточки */
const style = computed<CSSProperties>(() => ({ width: props.width, minWidth: minWidth.value }));

const parameterColumns = computed(() => {
  const filtredColumns = props.columns.filter(
    (column) => parameterTypes.includes(column.Type) && props.viewSettings.includes(column.Type),
  );
  return filtredColumns.map((column) => {
    return new ListItemModel({
      Type: column.Type,
      Width: column.Width,
      MinWidth: column.MinWidth,
    });
  });
});

const statusColumn = computed(() => {
  const column = props.columns.find(
    (column) =>
      column.Type === TaskViewElementEnum.Status && props.viewSettings.includes(column.Type),
  );

  return column
    ? new ListItemModel({
        Type: column.Type,
        Width: column.Width,
        MinWidth: column.MinWidth,
      })
    : null;
});

const elementsStyles = computed<
  Record<'Project' | 'Namespace' | 'Queue' | 'Id' | 'Name' | 'Status', CSSProperties>
>(() => {
  const projectColumn = getColumnByViewElement(TaskViewElementEnum.Project);
  const namespaceColumn = getColumnByViewElement(TaskViewElementEnum.Namespace);
  const queueColumn = getColumnByViewElement(TaskViewElementEnum.Queue);
  const idColumn = getColumnByViewElement(TaskViewElementEnum.Number);
  const status = getColumnByViewElement(TaskViewElementEnum.Status);
  const nameColumn = getColumnByViewElement(TaskViewElementEnum.Name);

  return {
    Project: {
      width: projectColumn?.Width ?? '1.5rem',
      minWidth: projectColumn?.MinWidth,
    },
    Namespace: {
      width: namespaceColumn?.Width ?? '1.5rem',
      minWidth: namespaceColumn?.MinWidth,
    },
    Queue: {
      width: queueColumn?.Width ?? '13rem',
      minWidth: queueColumn?.MinWidth,
    },
    Id: {
      width: idColumn?.Width ?? '5rem',
      minWidth: idColumn?.MinWidth,
    },
    Status: {
      width: status?.Width ?? '5rem',
      minWidth: status?.MinWidth,
    },
    Name: {
      width: nameColumn?.Width ?? '65.8125rem',
      minWidth: nameColumn?.MinWidth,
    },
  };
});
// #endregion computed

// #region Хуки жизненного цикла
onMounted(() => {
  if ($wrapper.value) {
    $wrapper.value.addEventListener('scroll', onScroll);
  }
});
onUnmounted(() => {
  if ($wrapper.value) {
    $wrapper.value.removeEventListener('scroll', onScroll);
  }
});
// #endregion Хуки жизненного цикла

// #region Функции
/**При скролле обертки */
const onScroll = () => {
  $contextMenu.value?.close();
};

const getColumnByViewElement = (viewElement: TaskViewElementEnum) => {
  return props.columns.find((column) => column.Type === viewElement);
};

/**Получение компонента для параметра задачи */
const getTaskParameterComponent = (type: TaskViewElementEnum) => {
  switch (type) {
    case TaskViewElementEnum.Difficulty:
      return TaskCardDifficulty;
    case TaskViewElementEnum.Priority:
      return TaskCardPriority;
    case TaskViewElementEnum.Status:
      return TaskCardStatus;
    case TaskViewElementEnum.Performers:
      return TaskCardPerformers;
    case TaskViewElementEnum.EstimateTime:
      return TaskCardEstimateTime;
    case TaskViewElementEnum.WorkTime:
      return TaskCardWorkTime;
  }
};

/**Открыть карту проекта */
const openSchema = computed(() => {
  return router.resolve({
    name: 'project-map',
    params: {
      taskId: props.task.Id,
      projectId: props.task.ProjectId,
    },
  });
});

/**Открыть детальную страницу */
const goToDetail = computed(() => {
  return router.resolve({
    name: 'task-detail',
    params: {
      projectId: props.task.ProjectId,
      queueId: props.task.Queues[0].Id,
      taskId: props.task.Id,
      namespaceId: props.task.NamespaceInfo[0].Id,
    },
  });
});
/**Добавить оповещение */
const addNotification = (message: string, isError: boolean = false, life?: number) => {
  TirPmNotificationEventBus.emit(
    'add',
    new TirPmNotificationModel({
      title: message,
      type: isError ? 'error' : 'success',
      group: 'teammate-card',
      life: life ?? 2000,
      closable: true,
    }),
  );
};

/**Скопировать название и ссылку */
const copyNameAndLinkTask = async () => {
  if (!props.task.ProjectId || !props.task.Queues[0].Id) return;

  const route = router.resolve({
    name: 'task-detail',
    params: {
      projectId: props.task.ProjectId,
      namespaceId: props.task.NamespaceInfo[0].Id,
      queueId: props.task.Queues[0].Id,
      taskId: props.task.Id,
    },
  });

  if (!route?.href) return;

  const origin =
    window.location.hostname === 'localhost' ? 'https://beta.hub-pm.com' : window.location.origin;

  const link = origin + route.href;
  const textContent = `${props.task.Id} ${props.task.Name}`;

  if (await copyToClipboard(`[${textContent}](${link})`)) {
    $contextMenu.value.close();
    addNotification('Название и ссылка тикета скопирована');
    console.log(6);
  }
};
/**Открыть окно удаления задачи */
const openRemoveModal = (id: number, name: string) => {
  $removeTaskModal.value?.open(id, name);
};
/**Удаление задачи из списка */
const onSuccessRemove = (taskId: number) => {
  emit('removeTask', taskId);
};

/**Скопировать название */
const copyNameTask = async () => {
  const taskData = {
    number: props.task.Id,
    name: props.task.Name,
  };

  if (await copyToClipboard(taskData.number + ' ' + taskData.name)) {
    $contextMenu.value.close();
    addNotification('Название тикета скопировано');
  }
};
/**Перетаскивается ли элемент */
const waitMouseUp = () => {
  return new Promise<void>((resolve) => {
    isDragging.value = false;

    const startTime = Date.now();

    const onMouseUp = () => {
      const duration = Date.now() - startTime;

      isDragging.value = duration >= 300;

      document.removeEventListener('mouseup', onMouseUp);
      isLink.value = true;
      resolve();
    };

    document.addEventListener('mouseup', onMouseUp, { once: true });
  });
};
/**Открыть задачу в шторке */
const openInDrawer = async () => {
  isLink.value = false;
  await waitMouseUp();
  if (!props.task.ProjectId || !props.task.HasAccessToTask || isDragging.value) return;
  $contextMenu.value?.close();
  emit('openTaskDrawer', props.task.Id);
  isLink.value = true;
};
// #endregion Функции
</script>
<template>
  <TirPmContextMenu
    ref="$contextMenu"
    min-width="24.25rem"
    maxHeight="max-content"
    :trigger-buttons="[TirPmContextMenuMouseButtonEnum.Right]"
  >
    <TirPmRouterLink
      :isDisabled="
        !props.task.ProjectId ||
        !props.task.Queues.length ||
        !props.task.NamespaceInfo.length ||
        !props.task.HasAccessToTask ||
        !isLink
      "
      :to="goToDetail"
      :class="[
        'task-card',
        { 'task-card__data-table': monitoringPage },
        { 'is-disabled': isDisabled, 'is-fill': isFill },
        { 'task-card__change-access': changeAccess },
        { 'task-card__order-style-task': task.IsHighlighted },
        { 'task-card__order-style-task--by-employee': task.IsHighlighted && task.ZmkType === 2 },
        {
          'task-card__order-style-task--by-employee-no-access':
            task.IsHighlighted && task.ZmkType === 2 && !task.HasAccessToTask,
        },
        { 'task-card__order-style-task--no-access': !task.HasAccessToTask },
        {
          'task-card__deadline-missed':
            !!props.selectPerformer?.UserId && props.task.EstimateTime < props.task.WorkTime,
        },
        {
          'task-card__deadline-success':
            !!props.selectPerformer?.UserId &&
            props.task.EstimateTime > props.task.WorkTime &&
            props.task.WorkTime > 0,
        },
        {
          'task-card__deadline-no-estimated-time':
            !!props.selectPerformer?.UserId && !props.task.EstimateTime && props.task.WorkTime > 0,
        },
      ]"
      :style="style"
      @mousedown.left.prevent="openInDrawer"
      @mousedown.prevent
      @dragstart.prevent
      @mousedown.middle.prevent
    >
      <div
        class="task-card_project"
        v-if="viewSettings?.includes(TaskViewElementEnum.Project)"
        :style="elementsStyles.Project"
      >
        <TirPmTooltipElement v-if="task?.Project" width="12.5rem">
          <TirPmIconView
            :size="24"
            is-rounded
            :color-code="task.Project.PreviewColor"
            :image-load-url="task.Project.ImageUrl"
            :is-bordered="false"
          >
            <template #mainSlot>
              {{ task.Project.Name?.[0] }}
            </template>
          </TirPmIconView>
          <template #content>
            <div class="task-card_tooltip-content">
              {{ task.Project.Name }}
            </div>
          </template>
        </TirPmTooltipElement>
      </div>
      <div
        class="task-card_namespace"
        v-if="viewSettings?.includes(TaskViewElementEnum.Namespace)"
        :style="elementsStyles.Namespace"
      >
        <TirPmTooltipElement v-if="task?.NamespaceInfo?.length">
          <NamespaceIconView
            :icon="task.NamespaceInfo[0].IconUrl"
            :color="task.NamespaceInfo[0].Color"
          />
          <template #content>
            <div class="task-card_tooltip-content">
              {{ task.NamespaceInfo[0].Name }}
            </div>
          </template>
        </TirPmTooltipElement>
      </div>
      <div
        class="task-card_queue"
        v-if="viewSettings?.includes(TaskViewElementEnum.Queue)"
        :style="elementsStyles.Queue"
      >
        <TirPmTooltipElement class="task-card_tooltip-block">
          <template #default v-if="task?.Queues?.length">
            <ListBulletIcon class="task-card_icon" />
            <div v-text="task.Queues[0].Name" class="task-card_queue_text" />
          </template>
          <template #content>
            {{ task.Queues[0].Name }}
          </template>
        </TirPmTooltipElement>
      </div>
      <div
        v-if="viewSettings?.includes(TaskViewElementEnum.Number)"
        v-text="task.Id"
        class="task-card_id"
        :style="elementsStyles.Id"
      />
      <div
        class="task-card_status"
        v-if="isStatusLeftBlock && viewSettings?.includes(TaskViewElementEnum.Status)"
      >
        <TaskCardStatus
          :element="statusColumn"
          :monitoringTicketPage="monitoringTicketPage"
          :task="task"
        />
      </div>
      <div
        class="task-card_name"
        v-if="viewSettings?.includes(TaskViewElementEnum.Name)"
        :style="elementsStyles.Name"
      >
        <Bars3BottomLeftIcon v-if="!hideIconName" class="task-card_icon" />
        <div v-text="task.Name" class="task-card_name_text" />
      </div>
      <Component
        class="task-card_right"
        v-for="(element, index) in parameterColumns"
        :key="index"
        :is="getTaskParameterComponent(element.Type)"
        :isStatusLeftBlock="isStatusLeftBlock"
        :shortInfoParameter="shortInfoParameter"
        :element="element"
        :monitoringTicketPage="monitoringTicketPage"
        :task="task"
      />

      <div
        v-if="haveLeftSlot"
        :class="['task-card_left', { 'slot-left-visible': slotLeftVisible }]"
        @click.stop.prevent
        @mousedown.stop
      >
        <slot name="left" />
      </div>
    </TirPmRouterLink>
    <template #content>
      <RouterLink :to="goToDetail" v-if="task.HasAccessToTask" target="_blank">
        <TirPmContextMenuButton class="task-card__menu-item">
          <ArrowTopRightOnSquareIcon class="task-card__icon" />
          Открыть в новой вкладке
        </TirPmContextMenuButton>
      </RouterLink>
      <RouterLink :to="openSchema" v-if="task?.ProjectId && task.HasAccessToTask">
        <TirPmContextMenuButton class="task-card__menu-item">
          <MapIcon class="task-card__icon" />
          Посмотреть карту проекта
        </TirPmContextMenuButton>
      </RouterLink>
      <TirPmContextMenuButton @click="copyNameAndLinkTask" class="task-card__menu-item">
        <ClipboardDocumentIcon class="task-card__icon" />
        Скопировать название и ссылку
      </TirPmContextMenuButton>
      <TaskHistoryOrder :userId="user?.Id" :taskId="task?.Id" />
      <PerformerListByOrder
        v-if="task.Performers.length"
        :taskInfo="task"
        @closePopover="$contextMenu.close()"
      />
      <TirPmContextMenuButton
        v-if="(isProjectAdmin || isOwner) && !hideOpenAndDelete"
        @click="openRemoveModal(task.Id, task.Name)"
      >
        <div class="task-card__menu-item">
          <TrashIcon class="task-card__menu-icon task-card__negative" />
          <span class="task-card__negative">Удалить тикет</span>
        </div>
      </TirPmContextMenuButton>
    </template>
    <RemoveTaskModal ref="$removeTaskModal" @successRemove="onSuccessRemove" />
  </TirPmContextMenu>
</template>
<style lang="scss" scoped>
.task__popover-title {
  padding: 1rem;
  font-size: 0.75rem;
}
.task-card {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  height: 3rem;
  padding: 0 0.625rem;
  background: var(--bg-base);
  border: 0.063rem solid var(--neutral-translucent-10);
  border-radius: 0.563rem;
  color: var(--text-primary, #272d37);
  &__data-table {
    justify-content: space-between;
  }
  &:hover {
    border-color: var(--neutral-100, #272d37);
  }
  &_tooltip-block {
    width: max-content !important;
    overflow: hidden;
    text-overflow: ellipsis;
    text-wrap: nowrap;
  }

  &__history-order {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }
  &__menu-item--popover {
    width: 100% !important;
  }
  &__order-style-task {
    border: 0.1563rem solid var(--text-info);
    background-color: var(--bg-info, #e4f0ff);
  }

  &__order-style-task--by-employee {
    border: none;
    background-color: var(--bg-info, #e4f0ff);
  }
  &__order-style-task--by-employee-no-access {
    border: none !important;
    background-color: var(--neutral-translucent-10, #272d371a);
  }
  &__order-style-task--no-access {
    cursor: not-allowed;
    border: 0.1563rem solid var(--text-tertiary, #93969b);
    background-color: var(--neutral-translucent-10, #272d371a);
  }
  &__change-access {
    border-width: 0.125rem;
    cursor: grabbing;
  }

  &__deadline-missed {
    background-color: var(--bg-negative, #ffebeb);
  }
  &__deadline-success {
    background-color: var(--bg-positive, #dff8e5);
  }
  &__deadline-no-estimated-time {
    background-color: var(--bg-secondary, #f8f6f9);
  }

  &__icon {
    width: 1.5rem;
    min-width: 1.5rem;
    height: 1.5rem;
  }

  &_right {
    justify-content: center;
  }

  &_queue {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    color: var(--text-secondary);
    font-size: 0.75rem;
    overflow: hidden;
    text-overflow: ellipsis;
    text-wrap: nowrap;

    &_text {
      max-width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
      text-wrap: nowrap;
    }
  }
  &_icon {
    width: 1rem;
    height: 1rem;
    color: var(--text-secondary);
    flex-shrink: 0;
  }
  &_id {
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--text-primary, #272d37);
  }
  &_name {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    font-size: 1rem;
    font-weight: 400;
    color: var(--text-primary, #272d37);

    &_text {
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2; /* Ограничиваем текст двумя строками */
      overflow: hidden;
      text-overflow: ellipsis;
      word-break: break-all;
    }
  }

  &_left {
    z-index: 1;
    display: block;
    position: absolute;
    top: 50%;
    right: 100%;
    padding-right: 0.5rem;
    transform: translateY(-50%);
    opacity: 0;
    transition: 0.1s ease-in-out;

    &.slot-left-visible {
      opacity: 1;
    }
  }

  &_tooltip-content {
    display: flex;
    justify-content: center;
    overflow-wrap: break-word;
    border-radius: 0.5rem;
    color: var(--text-primary-inverted, #e9eaeb);
    font-size: 0.75rem;
    font-weight: 400;
    line-height: 1rem;
  }

  &:hover {
    .task-card_left {
      opacity: 1;
    }
  }

  &.is-disabled {
    .task-card_id,
    .task-card_name {
      color: var(--text-tertiary);
    }
  }

  &.is-fill {
    background-color: var(--bg-tertiary, #f4f4f5);
  }

  &__animation-enter-active,
  &__animation-leave-active {
    transition: 0.3s;
    opacity: 1;
  }
  &__animation-enter-from,
  &__animation-leave-to {
    transition: 0.3s;
    opacity: 0;
  }
}
</style>

<style lang="scss">
.task-card__menu-item .tir-pm-context-menu-button__middle {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
</style>
